import {
  ApolloError,
  DataProxy,
  MutationHookOptions,
  useApolloClient
} from "@apollo/client"
import {
  Position,
  PositionOptions,
  PositionedObj,
  assignPositions,
  getPositionUpdates,
  sortByPosition
} from "@pathwright/utils/src/positioning/fractional-positioning"
import { DocumentNode, FragmentDefinitionNode } from "graphql"
import React, { useCallback } from "react"
import {
  PositionedObjectPositionInput,
  PositioningContextFragment,
  PositioningContextInput,
  useDeletePositioningContextMutation,
  usePositioningContextQuery,
  useUpdatePositionedObjectsMutation
} from "@pathwright/graphql-client/src/generated"

type PositioningContextValue<T extends PositionedObject> = {
  positioningContext: PositioningContextFragment | null
  positioningContextQuery: ReturnType<typeof usePositioningContextQuery>
  positionedObjects: PositionedObject<T>[]
  updatePositionedObject: UpdatePositionedObject<T>
  getPositionedObjectFragment: GetPositionedObjectFragment<T>
}

// Position can be null (for unpositioned objects).
export type PositionedObject<T = PositionedObj> = T & {
  position: Position
  // NOTE: string is too specific since it doesn't match literals (like "Registration").
  // Also, we're conditionally requiring the __typename field, though completely expecting
  // it's existance. This is because the field is optional on the generated types, but if
  // we were to require it, it would need to be supplied in many other contexts, which we
  // don't care to do.
  __typename?: any
}

type PositioningContextProviderProps<T extends PositionedObject> = {
  items: T[]
  itemKey: string
  // For updating the cache optimistically for immediate response.
  // TODO: need to handle updating the items list in the cache.
  // This could possibly be handled in InMemoryCache merge functions.
  itemFragmentDocument: DocumentNode
  // The context within which we are positioning the items.
  context: PositioningContextInput
  // Variables passed to readFragment/writeFragment; defaults to { positioningContext: context }.
  fragmentVariables?: Record<string, PositioningContextInput>
  // Presumably the UI listing the items.
  children: JSX.Element
}

type UpdatePositionedObject<T extends PositionedObject> = (
  options: {
    item: T
    index: number
    // Provide a list directly, taking precedence over getList.
    list?: T[]
    // Provide a list via fn that takes the current list of items.
    getList?: (list: T[]) => T[]
    // Force position the entire list of positioned items which means we delete the existing PositioningContext and create a new one.
    // This can be helpful when we encounter an internal server error preventing us from updating the positions of the items.
    forcePosition?: boolean
  } & Omit<MutationHookOptions, "variables" | "onError">
) => Promise<void>

type GetPositionedObjectFragment<T extends PositionedObject> = (
  id: number
) => DataProxy.Fragment<any, T>

// Sort objs by position, taking into account null positions, sorting them last.
// This can be used to order the objs in state or writing the list into the cache.
// Its too complex to automatiacally update the cache for queries, so not providing
// that out of the box.
export function sortPositionedObjects<T extends any>(
  positionedObjects: PositionedObject<T>[]
): T[] {
  // We have to finagle the typing to insist that PositionedObject<T> is acceptable
  // as PositionedObject[] then cast back to PositionedObject<T> to preserve typing.
  // Since sortByPosition doesn't mutate the list objects, this is safe.
  return sortByPosition(
    positionedObjects as PositionedObject[]
  ) as PositionedObject<T>[]
}

function hasGraphQLErrorMessage(error: unknown, message: string) {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors?.some(
      (graphQLError) =>
        graphQLError.message.toLowerCase() === message.toLowerCase()
    )
  )
}

export const PositioningContext = React.createContext<
  PositioningContextValue<any>
>(null as unknown as PositioningContextValue<any>)

export const usePositioningContext = () => React.useContext(PositioningContext)

// Wrap a list of positioned objects providign a means to update an item's position.
export function PositioningContextProvider<T extends PositionedObject>({
  items,
  itemKey,
  itemFragmentDocument,
  context,
  fragmentVariables: fragmentVariablesProp,
  children
}: PositioningContextProviderProps<T>) {
  const client = useApolloClient()
  const fragmentVariables = fragmentVariablesProp ?? {
    positioningContext: context
  }
  const positioningContextQuery = usePositioningContextQuery({
    variables: {
      input: context
    }
  })
  const positioningContext =
    positioningContextQuery.data?.positioningContext || null
  const [updatePositionedObjectsMutation] = useUpdatePositionedObjectsMutation()
  const [deletePositioningContextMutation] =
    useDeletePositioningContextMutation()

  const getPositionedObjectFragment: GetPositionedObjectFragment<T> =
    useCallback(
      (id) => {
        // Derive fragment name and typename from definition.
        const fraggmentDefinitionNode = itemFragmentDocument
          .definitions[0] as FragmentDefinitionNode
        const typename = fraggmentDefinitionNode.typeCondition.name.value
        const fragmentName = fraggmentDefinitionNode.name.value

        const fragment = {
          id: `${typename}:${id}`,
          fragment: itemFragmentDocument,
          // Sometimes we must specify the fragmentName to avoid conflicts.
          fragmentName
        }

        return fragment
      },
      [itemFragmentDocument]
    )

  // Executes a mutation to update the position of the provided item. The index is
  // used to determine the item's new position in the list.
  const updatePositionedObject: UpdatePositionedObject<T> = useCallback(
    async ({
      item,
      index,
      list,
      getList,
      forcePosition = false,
      ...mutationOptions
    }) => {
      if (forcePosition) {
        try {
          await deletePositioningContextMutation({
            variables: {
              context
            }
          })
        } catch (error) {
          // We ignore the error if the positioning context is not found.
          // The backend throws this error because we're attempting to delete a context
          // that doesn't exist. Safe to continue, since we'll create a new context.
          if (!hasGraphQLErrorMessage(error, "Positioning context not found")) {
            throw error
          }
        }
      }

      // Get the list, conditionally based on the list props.
      list ??= getList?.(items) || items

      const positionOptions = {
        list,
        item,
        index
        // TODO: remove the need for this override. It seems TS can't know
        // that the items in list T[] match the same typing of the item T.
      } as PositionOptions<T>
      // We get the position of the item as it will be once moved to the new index.
      const positionUpdates = getPositionUpdates(positionOptions)
      // Get list of items with newly assigned positions.
      const positionedItems = assignPositions(positionOptions)
      // Get list of sorted items.
      const sortedItems = sortPositionedObjects(positionedItems)

      // Key the item with id.
      function getObjKey(item: T) {
        return `${itemKey}:${item.id}`
      }

      // Get list of obj positions to send in mutation.
      const objPositionInputs = forcePosition
        ? sortedItems
            .filter((item) => item.position)
            .map((item) => {
              return {
                objKey: getObjKey(item),
                objPosition: item.position
              }
            })
        : positionUpdates.map(({ item: positionedItem, position }) => {
            return {
              objKey: getObjKey(positionedItem),
              objPosition: position
            }
          })

      // Updates cache as side-effect while reducing the positions to a list of
      // previous positions.
      function updateCache(
        objPositionInputs: PositionedObjectPositionInput[]
      ): PositionedObjectPositionInput[] {
        return objPositionInputs.reduce((acc, objPositionInput) => {
          const itemId = objPositionInput.objKey.split(":").slice(-1)[0]

          // Let's "optimisically" update the cache with the new position.
          // Note: we can't use apollo client's optimisticResponse since that's for
          // providing optimistic result of a mutation, but we're updating something else.

          const fragment = getPositionedObjectFragment(itemId)
          const data = client.readFragment<T>({
            ...fragment,
            variables: fragmentVariables
          })

          if (!data) {
            console.error(
              `No fragment data found for itemId: "${itemId}". Check the fragment definition and ensure the \`position\` key is present.`
            )
            return acc
          }

          // If the position key is not found in the fragment data, we log an error. Without the position key
          // we cannot update the position in the cache and subsequent updates will likely fail due to stale data (client vs server mismatch).
          if (!("position" in data)) {
            console.error(
              `No \`position\` key found in fragment data for itemId: "${itemId}". Check the fragment definition and ensure the \`position\` key is present.`
            )
            return acc
          }

          // Record the previous position.
          acc.push({
            ...objPositionInput,
            objPosition: data.position
          })

          try {
            // Write the new position.
            client.writeFragment({
              ...fragment,
              variables: fragmentVariables,
              data: {
                ...data,
                position: objPositionInput.objPosition
              }
            })
          } catch (error) {
            console.error("Error writing fragment", error)
          }

          return acc
        }, [] as PositionedObjectPositionInput[])
      }

      // Record previous item positions in case we need to roll back.
      const prevObjPositionInputs = updateCache(objPositionInputs)

      try {
        // Now let's actually execute the mutation.
        await updatePositionedObjectsMutation({
          variables: {
            context,
            positions: objPositionInputs
          },
          ...mutationOptions
        })
      } catch (error) {
        const hasInternalServerError = hasGraphQLErrorMessage(
          error,
          "Internal server error"
        )

        // When we encounter an internal server error, we attempt to force position the list which involves
        // deleting the PositioningContext, then creating a new one and re-positioning the items.
        if (hasInternalServerError && !forcePosition) {
          await updatePositionedObject({
            item,
            index,
            list,
            getList,
            forcePosition: true
          })
        } else {
          // Roll back this update since it failed.
          updateCache(prevObjPositionInputs)
        }
      }
    },
    [
      deletePositioningContextMutation,
      updatePositionedObjectsMutation,
      getPositionedObjectFragment,
      itemKey,
      items,
      context,
      fragmentVariables
    ]
  )

  return (
    <PositioningContext.Provider
      value={{
        positioningContext,
        positioningContextQuery,
        positionedObjects: items,
        updatePositionedObject,
        getPositionedObjectFragment
      }}
    >
      {children}
    </PositioningContext.Provider>
  )
}
